import { Button, Divider, Form, Input, Modal, Select, Space, TableProps, Tabs, Tag } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice';
import {
  apiAddListZaloAccountSessions,
  apiAddListZaloAccounts,
  apiGetListZaloAccountSessions,
  apiGetListZaloAccounts,
  apiUpdateListZaloAccounts,
  apiDeleteListZaloAccounts,
  apiUpdateListZaloAccountSessions,
  apiGetListUsers,
} from '../../api'
import { DeleteOutlined, EditOutlined, EyeOutlined, LeftCircleOutlined, LoginOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons'

import { toastError, toastSuccess } from '../../utils/toast'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { useLoading } from '../../context/LoadingContext'

const TAG_STYLE = { width: 100, textAlign: 'center' as const, marginRight: 0 }

const renderType = (type: string) => {
  if (type === 'ACTIVE') return <Tag style={TAG_STYLE} color="green">Hoạt động</Tag>
  if (type === 'INACTIVE') return <Tag style={TAG_STYLE} color="red">Không hoạt động</Tag>
  if (type === 'CLOSED') return <Tag style={TAG_STYLE} color="orange">Đã đóng</Tag>
  if (type === 'PERSONAL') return <Tag style={TAG_STYLE} color="gray">PERSONAL</Tag>
  if (type === 'OA') return <Tag style={TAG_STYLE} color="blue">OA</Tag>
  if (type === 'QR') return <Tag style={TAG_STYLE} color="geekblue">QR</Tag> 
  return
}

function toArray(value: any): any[] {
  return Array.isArray(value) ? value : []
}

function toIntNumber(value: any): number | null {
  const num = Number(value)
  if (!Number.isFinite(num)) {
    return null
  }
  return num
}

function isActiveStatus(status: any): boolean {
  return String(status || "").toUpperCase() === "ACTIVE";
}

export default function Accounts() {
  const [qr, setQr] = useState<string | null>(null)
  const [status, setStatus] = useState<string>('Đang chờ tạo QR...')
  const [modalLogin, setModalLogin] = useState<boolean>(false)
  const socketRef = useRef<Socket | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
  const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
  const { setIsLoading } = useLoading()
  const [tabTransaction, setTabTransaction] = useState<any>({})
  const [zaloAccounts, setZaloAccounts] = useState<any[]>([])
  const [zaloAccountSessions, setZaloAccountSessions] = useState<any[]>([])
  const [user, setUser] = useState<any>([])
  const authState = useSelector(authSelector)
  const crmUserId = String(authState?.account?.id || '').trim()
  const crmUserIdNumber = toIntNumber(crmUserId)
  const [modelUpdateAccount, setModelUpdateAccount] = useState<any>({ isOpen: false, data: null })
  const [updateAccount, setUpdateAccount] = useState<any>(null)
  const [modelDeleteAccount, setModelDeleteAccount] = useState<any>({ isOpen: false, data: null })
  const [currentSessionContext, setCurrentSessionContext] = useState<{ accountId: number | null; sessionId: any | null }>({
    accountId: null,
    sessionId: null,
  })
  const [search, setSearch] = useState<any>({})
  const [formSearch] = Form.useForm()


  const getCrmUserInfo = useCallback((userId: number) => {
    if (!user || user.length === 0) {
      return null
    }
    const userInfo = user.find((u: any) => Number(u?.id) === Number(userId))
    return userInfo || null
  }, [user])

  const formatDate = (isoString: any) => {
    const date = new Date(isoString);

    return date.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatPhone = (phone: any) => {
    if (!phone) return '';

    return phone.replace(/^\+84/, '0');
  };

  const zaloAccountColumns: TableProps<any>['columns'] = [
    {
      key: 'id',
      title: 'Mã account',
      dataIndex: 'id',
    },
    {
      key: 'displayName',
      title: 'Tên hiển thị',
      dataIndex: 'displayName',
    },
    {
      key: 'zaloUid',
      title: 'Zalo UID',
      dataIndex: 'zaloUid',
    },
    {
      key: 'accountType',
      title: 'Loại',
      dataIndex: 'accountType',
      render: value => renderType(value),
    },
    {
      key: 'phone',
      title: 'Số điện thoại',
      dataIndex: 'phone',
      render: value => formatPhone(value),
    },
    {
      key: 'status',
      title: 'Trạng thái',
      dataIndex: 'status',
      render: value => renderType(value),
    },
    {
      key: 'userName',
      title: 'User phụ trách',
      dataIndex: 'userName',
      render: (_, record) => {
        const activeSessions = toArray(zaloAccountSessions).filter((session: any) => {
          return (
            Number(session?.zaloAccountId) === Number(record?.id) &&
            isActiveStatus(session?.status)
          )
        })

        if (activeSessions.length === 0) {
          return 'Không có'
        }

        const latestSession = activeSessions.reduce((latest: any, current: any) => {
          const latestTime = new Date(latest?.lastActiveAt || latest?.startedAt || 0).getTime()
          const currentTime = new Date(current?.lastActiveAt || current?.startedAt || 0).getTime()
          return currentTime > latestTime ? current : latest
        })
        const userInfo = getCrmUserInfo(Number(latestSession?.userId))
        return userInfo ? userInfo.full_name : 'Không có'
      }
    },
    {
      key: 'lastUsedAt',
      title: 'Sử dụng gần nhất',
      dataIndex: 'lastUsedAt',
      render: value => value ? formatDate(value) : 'N/A',
    },
    {
      key: 'updated_at',
      title: 'Cập nhật lúc',
      dataIndex: 'updated_at',
      render: value => value ? formatDate(value) : 'N/A',
    },
    {
      key: 'action',
      title: 'Tác vụ',
      render: data => renderAction(data)
    },
    (
      authState?.account?.canUpdateListZaloAccounts || authState?.account?.canDeleteListZaloAccounts
    ) ? {
      key: 'update',
      title: 'Cập nhật',
      render: data => (
        <Space>
          { authState?.account?.canUpdateListZaloAccounts && (<Button onClick={() => {
            setModelUpdateAccount({ isOpen: true, data })
            setUpdateAccount({
              displayName: data?.displayName,
              note: data?.note,
            })
          }}
            style={{ width: 132, background: '#1890ff', color: 'white' }}>
            <EditOutlined /> Chỉnh sửa
          </Button>)}
          
          { authState?.account?.canDeleteListZaloAccounts && (
            <Button onClick={() => {
              setModelDeleteAccount({ isOpen: true, data })
            }}
              style={{ width: 132, background: '#ae2d2d', color: 'white' }}>
                <DeleteOutlined /> Xóa
              </Button>
          )}
        </Space>
      )
    } : {
            key: 'update',
            title: 'Cập nhật',
            render: () => <span style={{ color: 'gray' }}>Không có quyền thao tác</span>
        },
  ]

  const zaloAccountSessionColumns: TableProps<any>['columns'] = [
    {
      key: 'sessionCode',
      title: 'Mã phiên',
      dataIndex: 'sessionCode',
    },
    {
      key: 'user',
      title: 'Nhân sự',
      dataIndex: 'user',
      render: (_, record) => {
        const userInfo = getCrmUserInfo(Number(record.userId))
        return userInfo ? userInfo.full_name : 'N/A'
      }
    },
    { 
      key: 'startMethod',
      title: 'Phương thức',
      dataIndex: 'startMethod',
      render: value => renderType(value),
    },
    {
      key: 'status',
      title: 'Trạng thái',
      dataIndex: 'status',
      render: value => renderType(value),
    },
    {
      key: 'startedAt',
      title: 'Bắt đầu',
      dataIndex: 'startedAt',
      render: value => value ? formatDate(value) : 'N/A',
    },
    {
      key: 'lastActiveAt',
      title: 'Hoạt động cuối',
      dataIndex: 'lastActiveAt',
      render: value => value ? formatDate(value) : 'N/A',
    },
    {
      key: 'ipAddress',
      title: 'IP',
      dataIndex: 'ipAddress',
    },
    {
      key: 'userAgent',
      title: 'Thiết bị',
      dataIndex: 'userAgent',
    },
  ]

  const fetchUser = useCallback(async () => {
    try {
      setIsLoading(true)
      const resp = await apiGetListUsers({
        limit: defaultParams.LIMIT,
      })
      if (resp.status === 200) {
        setUser(resp.data?.users || [])
      }
    } catch (error: any) {
      console.log(error)
      toastError('Can not get user information!')
    } finally {
      setIsLoading(false)
    }
    }, [setIsLoading])

  const fetchZaloAccountSessions = useCallback(async (account: any) => {
    const accountList = toArray(account)
    const sessions = [] as any[]
    accountList.forEach((item: any) => {
      if (item.id && !sessions.includes(item.id)) {
        sessions.push(item.id)
      }
    })
    if (sessions.length === 0) {
      setZaloAccountSessions([])
      return
    }

    const results = await Promise.allSettled(
      sessions.map(async (accountId: number) => {
        const resp = await apiGetListZaloAccountSessions({
          page: 1,
          size: pageSize,
          zaloAccountId: accountId,
          orderBy: 'created_at desc',
        })
        if (resp?.status !== 200) return []
        return toArray(resp?.data?.zaloAccountSessions)
      })
    )

    const merged = results.flatMap(item => item.status === "fulfilled" ? item.value : [])
    setZaloAccountSessions(merged)
  }, [pageSize])

  const fetchZaloAccounts = useCallback(async () => {
    try {
      setIsLoading(true)
      const { managedAccountIds, ...apiSearch } = search || {}

      const resp = await apiGetListZaloAccounts({
        orderBy: 'created_at asc',
        page: currentPage,
        size: pageSize,
        ...apiSearch,
      })

      let accounts = toArray(resp?.data?.zaloAccounts)
      if (Array.isArray(managedAccountIds) && managedAccountIds.length > 0) {
        accounts = accounts.filter((a: any) => managedAccountIds.includes(Number(a?.id)))
      }

      if (resp.status === 200) {
        setZaloAccounts(accounts)
        await fetchZaloAccountSessions(accounts)
      }
    } catch (error: any) {
      console.log(error)
      toastError('Can not get Zalo accounts list!')
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, pageSize, setIsLoading, fetchZaloAccountSessions, search])

  // this function will get zalo account information from sessionData after successful login by QR, if it already exists in DB then return it, if not then create new one and return it
  const getOrCreateZaloAccount = useCallback(async (sessionData: any, displayName: string): Promise<any> => {
    const uidNumber = toIntNumber(sessionData?.uid)
    if (!uidNumber) {
      toastError('Can not find Zalo UID in session information.')
      return
    }

    const accountResp = await apiGetListZaloAccounts({
      page: 1,
      size: 1,
      zaloUid: uidNumber,
    })
    const accounts = toArray(accountResp?.data?.zaloAccounts)
    const existed = accounts.find((item: any) => Number(item?.zaloUid) === uidNumber)
    if (existed) {
      return existed
    }

    const nowIso = new Date().toISOString()
    const lastAction = Number(sessionData?.user?.lastActionTime || sessionData?.lastActionTime || 0)
    const lastUsedAt = Number.isFinite(lastAction) && lastAction > 0 ? new Date(lastAction).toISOString() : nowIso
    await apiAddListZaloAccounts({
      zaloAccounts: [
        {
          displayName,
          zaloUid: uidNumber,
          accountType: 'PERSONAL',
          phone: sessionData?.user?.phoneNumber || '',
          avatarUrl: sessionData?.user?.avatar || '',
          status: 'ACTIVE',
          note: `Auto-created from QR login by CRM user ${crmUserId}`,
          lastUsedAt,
        },
      ],
    })

    const refreshResp = await apiGetListZaloAccounts({
      page: 1,
      size: 1,
      zaloUid: uidNumber,
    })
    const refreshAccounts = toArray(refreshResp?.data?.zaloAccounts)
    const account = refreshAccounts.find((item: any) => Number(item?.zaloUid) === uidNumber)

    if (!account) {
      toastError('Failed to create Zalo account!')
      return
    }

    return account
  }, [crmUserId])

  // This function ensures that after successfully logging in with QR, there will be an ACTIVE session created for that user and corresponding zalo account.
  // If there is already an ACTIVE session, it will not create a new one but just update the context to reuse that session
  const ensureSessionForUser = useCallback(async (
    zaloAccountId: number,
    sessionData: any,
  ): Promise<'CREATED' | 'EXISTS' | 'BLOCKED'> => {
    const sessionResp = await apiGetListZaloAccountSessions({
      page: 1,
      size: 200,
      zaloAccountId: zaloAccountId,
      status: 'ACTIVE',
    })
    const accountSessions = toArray(sessionResp?.data?.zaloAccountSessions)
    // check if there is any ACTIVE session belonging to other user, if yes then do not allow to log in and show message
    const activeSessionByOtherUser = accountSessions.find((item: any) => {
      return (
        Number(item?.userId) !== crmUserIdNumber &&
        Number(item?.zaloAccountId) === zaloAccountId &&
        isActiveStatus(item?.status)
      )
    })

    if (activeSessionByOtherUser) {
      const ownerInfo = getCrmUserInfo(Number(activeSessionByOtherUser?.userId))
      const ownerName = ownerInfo?.full_name || `ID ${activeSessionByOtherUser?.userId}`
      toastError(`This Zalo account is currently used by ${ownerName}. You cannot log in.`)
      return 'BLOCKED'
    }

    // If there is already an ACTIVE session belonging to this user, do not create a new one but just update the context to reuse that session
    const hasActiveSession = accountSessions.some((item: any) => {
      return (
        Number(item?.userId) === crmUserIdNumber &&
        Number(item?.zaloAccountId) === zaloAccountId &&
        isActiveStatus(item?.status)
      )
    })

    if (hasActiveSession) {
      const currentUserSessions = accountSessions
        .filter((item: any) => {
          return (
            Number(item?.userId) === crmUserIdNumber &&
            Number(item?.zaloAccountId) === zaloAccountId &&
            isActiveStatus(item?.status)
          )
        })
        .sort((a: any, b: any) => {
          const aTime = new Date(a?.lastActiveAt || a?.startedAt || 0).getTime()
          const bTime = new Date(b?.lastActiveAt || b?.startedAt || 0).getTime()
          return bTime - aTime
        })

      setCurrentSessionContext({
        accountId: zaloAccountId,
        sessionId: currentUserSessions[0]?.id || null,
      })
      return 'EXISTS'
    }

    // Create a new ACTIVE session for this user and corresponding zalo account
    const nowIso = new Date().toISOString()
    const expiredAtIso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    const cookieArray = Array.isArray(sessionData?.cookie) ? sessionData.cookie : []
    const sessionCode = cookieArray.find((item: any) => item?.key === 'zlogin_session')?.value
      || cookieArray[0]?.value
      || ''

    const resp = await apiAddListZaloAccountSessions({
      zaloAccountSessions: [
        {
          sessionCode,
          zaloAccountId,
          userId: crmUserIdNumber,
          startMethod: 'QR',
          status: 'ACTIVE',
          startedAt: nowIso,
          endedAt: nowIso,
          expiredAt: expiredAtIso,
          lastActiveAt: nowIso,
          ipAddress: sessionData?.publicIp || '',
          userAgent: sessionData?.userAgent || '',
          deviceInfo: sessionData?.imei || '',
          failureReason: '',
        },
      ],
    })

    if (resp.status !== 200) {
      toastError('Failed to create new session!')
      return 'BLOCKED'
    }

    const latestSessionResp = await apiGetListZaloAccountSessions({
      page: 1,
      size: 1,
      orderBy: 'created_at desc',
      zaloAccountId: zaloAccountId,
      userId: crmUserIdNumber,
    })
    const latestSession = toArray(latestSessionResp?.data?.zaloAccountSessions)[0]
    setCurrentSessionContext({
      accountId: zaloAccountId,
      sessionId: latestSession?.id || null,
    })

    return 'CREATED'
  }, [crmUserIdNumber, getCrmUserInfo])

  // This function ensures that after successfully logging in with QR, there will be an ACTIVE session created for that user and corresponding zalo account.
  const ensureDataAfterLogin = useCallback(async (sessionData: any): Promise<'CREATED' | 'EXISTS' | 'BLOCKED'> => {
    const displayName = sessionData?.user?.displayName || sessionData?.uid || 'bạn'
    const zaloAccount = await getOrCreateZaloAccount(sessionData, displayName)
    const zaloAccountId = toIntNumber(zaloAccount?.id)
    if (!zaloAccountId || !crmUserIdNumber) {
      toastError('Cannot determine zaloAccountId.')
      return 'BLOCKED'
    }

    const result = await ensureSessionForUser(zaloAccountId, sessionData)

    if(result === 'CREATED') {
      const resp = await apiUpdateListZaloAccounts({
        zaloAccounts: [
          {
            id: zaloAccountId,
            status: 'ACTIVE',
          }
        ]
      })

      if (resp.status !== 200) {
        toastError('Create session successful but failed to update account status!')
      }
    }

    await fetchZaloAccounts()
    return result
  }, [crmUserIdNumber, ensureSessionForUser, fetchZaloAccounts, getOrCreateZaloAccount])

  const disconnectSocket = useCallback(() => {
    if (!socketRef.current) {
      return
    }

    if (socketRef.current.connected && crmUserId) {
      socketRef.current.emit('cancel-qr', { userId: crmUserId })
    }

    socketRef.current.off('connect')
    socketRef.current.off('qr-generated')
    socketRef.current.off('qr-scanned')
    socketRef.current.off('qr-expired')
    socketRef.current.off('login-success')
    socketRef.current.off('login-failure')
    socketRef.current.disconnect()
    socketRef.current = null
  }, [crmUserId])

  const connectSocketAndAuthenticateByUserId = useCallback(() => {
    if (!crmUserId || !crmUserIdNumber) {
      setStatus('Không tìm thấy userId CRM. Vui lòng đăng nhập lại CRM.')
      return
    }

    disconnectSocket()

    const socket = io('https://koiosops.com', {
      transports: ['websocket'],
      path: '/zalo-login/socket.io',
    })

    socketRef.current = socket

    socket.on('connect', () => {
      // Each time reconnecting the socket, it will cancel the old QR (if any) and create a new QR
      // to avoid the case where the old QR is scanned and then the socket connects to the server, it will not receive that QR scanned event
      setStatus('Đang kết nối server...')
      setQr(null)
      socket.emit('cancel-qr', { userId: crmUserId })

      setStatus('Đang tạo mã QR mới...')
      socket.emit('get-qr', { userId: crmUserId })
    })

    socket.on('qr-generated', (qrImageBase64: string) => {
      setQr(qrImageBase64)
      setStatus('Vui lòng quét mã QR để đăng nhập.')
    })

    socket.on('qr-scanned', () => {
      setStatus('Đã quét mã. Vui lòng xác nhận trên điện thoại.')
    })

    socket.on('qr-expired', () => {
      setStatus('Mã QR đã hết hạn.')
    })

    socket.on('login-success', async (data: any) => {
      setQr(null)
      const displayName = data?.user?.displayName || 'bạn'
      setStatus(`Đăng nhập thành công! Xin chào, ${displayName}.`)
      console.log(data)
      try {
        const result = await ensureDataAfterLogin(data)
        if (result === 'CREATED') {
          toastSuccess('Login success and new session created.')
        }
        if (result === 'EXISTS') {
          toastSuccess('Login success. Session already exists, not creating new one.')
        }
      } catch (error) {
        console.log(error)
        const message = error instanceof Error
          ? error.message
          : 'Đăng nhập thành công nhưng đồng bộ dữ liệu vào hệ thống thất bại.'
        setStatus(message)
        toastError('Login success but failed to sync data!')
      }
    })

    socket.on('login-failure', () => {
      setStatus('Đăng nhập thất bại. Vui lòng thử lại.')
    })
  }, [crmUserId, crmUserIdNumber, disconnectSocket, ensureDataAfterLogin])

  const requestNewQR = useCallback(() => {
    if (!crmUserId) {
      setStatus('Không tìm thấy userId CRM. Vui lòng đăng nhập lại CRM.')
      return
    }

    setStatus('Đang tạo mã QR mới...')
    setQr(null)
    connectSocketAndAuthenticateByUserId()
  }, [connectSocketAndAuthenticateByUserId, crmUserId])

  // Update normally with fields: name, note
  // if there is status update, then need to check more:
  // 1. if the updated status is active, then need to check if there is any active session or not before updating.
  // 2. if the updated status is inactive, then need to close the active session at the same time.
  const handleUpdateAccount = async () => {
    try { 
      setIsLoading(true)

      const accountId = modelUpdateAccount?.data?.id
      if (!accountId) {
        toastError('Account ID is missing!')
        return
      }
      
      const infoResp = await apiUpdateListZaloAccounts({
        zaloAccounts: [
          {
            id: accountId,
            displayName: updateAccount?.displayName,
            note: updateAccount?.note,
          },
        ],
      })

      if (infoResp?.status !== 200) {
        toastError('Failed to update account information!')
        return
      }

      // if not updating status then stop here
      if (updateAccount?.status == null) {
        toastSuccess('Account information updated successfully.')
        setModelUpdateAccount({ isOpen: false, data: null })
        await fetchZaloAccounts()
        return
      }

      const respSession = await apiGetListZaloAccountSessions({
        orderBy: 'created_at desc',
        page: 1,
        size: 1,
        zaloAccountId: accountId,
        status: 'ACTIVE',
      })
      if (respSession?.status !== 200) {
        toastError('Failed to update account ACTIVE!')
        return
      }

      const activeSessions = toArray(respSession?.data?.zaloAccountSessions).length > 0
      // Handle logic by status
      if (updateAccount.status === 'ACTIVE') {
        if (!activeSessions) {
          toastError('Cannot activate account when there is no ACTIVE session!')
          return
        }

        const accResp = await apiUpdateListZaloAccounts({
          zaloAccounts: [{ id: accountId, status: 'ACTIVE' }]
        })

        if (accResp?.status !== 200) {
          toastError('Update account ACTIVE failed!')
          return
        }
        toastSuccess('Account information updated successfully.')
        setModelUpdateAccount({ isOpen: false, data: null })
        await fetchZaloAccounts()
      }

      if (updateAccount.status === 'INACTIVE') {
        // Close session first
        if (activeSessions) {
          const sessions = toArray(respSession?.data?.zaloAccountSessions)

          if (sessions.length > 0) {
            const sessionResp = await apiUpdateListZaloAccountSessions({
              zaloAccountSessions: sessions.map((s: any) => ({
                id: s.id,
                status: 'CLOSED'
              }))
            })

            if (sessionResp?.status !== 200) {
              toastError('Failed to close session!')
              return
            }
          }
        }

        // Update account status
        const accResp = await apiUpdateListZaloAccounts({
          zaloAccounts: [{ id: accountId, status: 'INACTIVE' }]
        })

        if (accResp?.status !== 200) {
          toastError('Update account INACTIVE failed!')
          return
        }

        toastSuccess('Account information updated successfully.')
        setModelUpdateAccount({ isOpen: false, data: null })
        await fetchZaloAccounts()
      }
    } catch (error) {
      console.log(error)
      toastError('Failed to update account information!')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    try {
      setIsLoading(true)
      const resp = await apiDeleteListZaloAccounts({
        zaloAccounts: [
          {
            id: modelDeleteAccount?.data?.id
          }
        ]
      })
      if (resp.status !== 200) {
        toastError('Failed to delete account due to active account status!')
        return
      }
      toastSuccess('Account deleted successfully.')
      if (Number(currentSessionContext?.accountId) === Number(modelDeleteAccount?.data?.id)) {
        setCurrentSessionContext({ accountId: null, sessionId: null })
      }
      setModelDeleteAccount({ isOpen: false, data: null })
      await fetchZaloAccounts()
    } catch (error) {
      console.log(error)
      toastError('Failed to delete account due to active account status!')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseModalLogin = useCallback(() => {
    setModalLogin(false)
    disconnectSocket()
  }, [disconnectSocket])

  const handleSearch = ({ displayName, zaloUid, phone, status }: any) => {
    if (!displayName && !zaloUid && !phone && !status) return
    if (phone) {
      phone = phone.replace(/^0+/, '+84')
    }
    setSearch({ displayName, zaloUid, phone, status })
    setCurrentPage(defaultParams.PAGE)
    setPageSize(defaultParams.LIMIT)
  }

  const handleClearSearch = () => {    
    formSearch.resetFields()
    setSearch({})
    setCurrentPage(defaultParams.PAGE)
    setPageSize(defaultParams.LIMIT)
  }

  // This function will find out the zalo accounts that the current logged in CRM user has active sessions.
  const handlerSearchAccountLoginByUser = async () => {
    try {
      const resp = await apiGetListZaloAccountSessions({
        page: 1,
        size: pageSize,
        userId: crmUserIdNumber,
        status: 'ACTIVE',
      })
      if (resp?.status !== 200) {
        toastError('Failed to fetch account sessions!')
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
        return
      }
      const sessions = toArray(resp?.data?.zaloAccountSessions)
      const managedAccountIds = Array.from(
        new Set(
          sessions
            .filter((s: any) => Number(s?.userId) === crmUserIdNumber && isActiveStatus(s?.status))
            .map((s: any) => Number(s?.zaloAccountId))
            .filter((id: number) => Number.isFinite(id) && id > 0)
        )
      )
      if (managedAccountIds.length === 0) {
        toastError("You don't have any active sessions!")
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
        return
      }
      setSearch({ managedAccountIds })
      setCurrentPage(defaultParams.PAGE)
      setPageSize(defaultParams.LIMIT)
    } catch (error) {
      toastError('Failed to search for accounts!')
      setSearch({})
      setCurrentPage(defaultParams.PAGE)
      setPageSize(defaultParams.LIMIT)
    }
  }

  useEffect(() => {
    fetchUser()
    fetchZaloAccounts()
  }, [fetchZaloAccounts, fetchUser])

  useEffect(() => {
    return () => {
      disconnectSocket()
    }
  }, [disconnectSocket])

  const handleShowDetail = ({data}: any) => {
    const accountId = Number(data?.id)
    const sessionsByAccount = toArray(zaloAccountSessions)
    .filter((session: any) => Number(session?.zaloAccountId) === accountId)
    .sort((a: any, b: any) => {
      const timeA = new Date(a?.created_at || a?.startedAt || 0).getTime()
      const timeB = new Date(b?.created_at || b?.startedAt || 0).getTime()
      return timeB - timeA
    })

    setTabTransaction({
      data: data,
      sessions: sessionsByAccount
    })
  }

  const renderAction = (data: any) => {
    return (
      <Button onClick={() => {
        handleShowDetail({data})
      }}
        style={{ width: 132, background: '#289e45', color: 'white' }}>
        <EyeOutlined /> Chi tiết
      </Button>
    )
  }

  const renderDetailAccount = (account: any) => {
    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <h1>{account?.displayName}</h1>
              <p>
                {renderType(account?.status)}
              </p>
              <p>{renderType(account?.accountType)}</p>
            </div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
              <p><b>Mã:</b> {account?.id}</p>
              <p><b>UID:</b> {account?.zaloUid}</p>
              <p><b>SĐT:</b> {formatPhone(account?.phone)}</p>
            </div>
            <div>
              <p><b>Sử dụng gần nhất:</b> {formatDate(account?.lastUsedAt)}</p>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            {/* This place will contain the interaction functions with zalo account sessions or configs in the future */}
          </div>
      </div>
      </>
    )
  }

  const renderDetail = (account: any, sessions: any) => {
    return (
      <>
        <Button onClick={() => setTabTransaction({})}
          icon={<LeftCircleOutlined />}
          style={{ background: 'gray', color: 'white' }}>
          Trở lại
      </Button>
      {renderDetailAccount(account)}
      <Tabs
        size='middle'
        items={[
          {
            key: 'info',
            label: 'Thông tin chung',
            children: 
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <p><b>Mã account:</b> {account?.id}</p>
                  <p><b>UID Zalo:</b> {account?.zaloUid}</p>
                  <p><b>Số điện thoại:</b> {formatPhone(account?.phone)}</p>
                  <p><b>Ghi chú:</b> {account?.note}</p>
                  <p><b>Ngày tạo:</b> {formatDate(account?.created_at)}</p>
                </div>
                <div style={{ flex: 1 }}>
                  <p><b>Tên hiển thị:</b> {account?.displayName}</p>
                  <p><b>Loại account:</b> {renderType(account?.accountType)}</p>
                  <p><b>Trạng thái:</b> {renderType(account?.status)}</p>
                  <p><b>Cập nhật lúc:</b> {formatDate(account?.updated_at)}</p>
                </div>
              </div>
          },
          {
            key: 'sessions',
            label: 'Phiên sử dụng',
            children: 
              <div>
                <CustomTable
                  namePage='session'
                  columns={zaloAccountSessionColumns}
                  dataSource={sessions}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  total={sessions.length}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>
          },
        ]}
        />
      </>
    )
  }

  const renderLoginAndAccount = () => {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>Danh sách Zalo Account</h1>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              onClick={() => {
                setModalLogin(true)
                setStatus('Đang kết nối tới máy chủ...')
                setQr(null)
                connectSocketAndAuthenticateByUserId()
              }}
              type='primary'
              disabled={!crmUserId}
            >
              <LoginOutlined /> Đăng nhập Zalo
            </Button>
          </div>
        </div>

        <Form form={formSearch} onFinish={handleSearch}>
           <div style={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: 20 }}>
            <Form.Item name="displayName" label="Tên hiển thị">
              <Input placeholder="Nhập tên hiển thị" />
            </Form.Item>
            <Form.Item name="zaloUid" label="UID Zalo">
              <Input placeholder="Nhập UID Zalo" />
            </Form.Item>
            <Form.Item name="phone" label="Số điện thoại">
              <Input placeholder="Nhập số điện thoại" />
            </Form.Item>
            <Form.Item name="status" label="Trạng thái">
              <Select placeholder="Chọn trạng thái">
                <Select.Option value="ACTIVE">ACTIVE</Select.Option>
                <Select.Option value="INACTIVE">INACTIVE</Select.Option>
              </Select>
            </Form.Item>
            <Space style={{ marginBottom: 24 }}>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                Tìm kiếm
              </Button>
              <Button type="primary" style={{ background: '#289e45', color: 'white' }} onClick={handlerSearchAccountLoginByUser} icon={<SearchOutlined />}>
                Tìm kiếm tài khoản do bạn quản lý
              </Button>
              <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
            </Space>
          </div>
        </Form>


        <Modal
          title='Zalo QR Code'
          open={modalLogin}
          onCancel={handleCloseModalLogin}
          footer={null}
        >
          <p>Trạng thái: {status}</p>
          {qr ? (
            <div style={{ textAlign: 'center' }}>
              <img src={qr} alt='Zalo QR Code' style={{ width: '100%', maxWidth: 320 }} />
            </div>
          ) : (
            !status.includes('thành công') && <p>Đang tải...</p>
          )}
          {(status.includes('hết hạn') || status.includes('thất bại')) && (
            <Button type='primary' onClick={requestNewQR}>Tạo lại mã QR</Button>
          )}
        </Modal>

        <CustomTable
          namePage='tài khoản zalo'
          columns={zaloAccountColumns}
          dataSource={zaloAccounts}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={zaloAccounts.length}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowKey='id'
        />
      </div>
    )
  }

  return (
    <>
      {tabTransaction?.data ? renderDetail(tabTransaction.data, tabTransaction.sessions) : renderLoginAndAccount()}
      <Modal
        title='Cập nhật Zalo Account'
        open={modelUpdateAccount?.isOpen}
        onCancel={() => setModelUpdateAccount({ isOpen: false, data: null })}
        footer={null}>
        <Divider />
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 10 }}>Tên hiển thị</div>
          <Input
            value={updateAccount?.displayName}
            onChange={e => setUpdateAccount((prev: any) => ({ ...prev, displayName: e.target.value }))}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 10 }}>Ghi chú</div>
          <Input
            value={updateAccount?.note}
            onChange={e => setUpdateAccount((prev: any) => ({ ...prev, note: e.target.value }))}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 10 }}>Trạng thái</div>
          <Select
            value={updateAccount?.status}
            onChange={value => setUpdateAccount((prev: any) => ({ ...prev, status: value }))}
            style={{ width: '100%' }}
            placeholder='Chọn trạng thái'
            options={[
              { label: 'ACTIVE', value: 'ACTIVE' },
              { label: 'INACTIVE', value: 'INACTIVE' },
            ]}
          />
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button onClick={handleUpdateAccount} type='primary' htmlType='submit'>Cập nhật</Button>
        </div>
      </Modal>
      <Modal
        title='Xóa Zalo Account'
        open={modelDeleteAccount?.isOpen}
        onCancel={() => setModelDeleteAccount({ isOpen: false, data: null })}
        footer={null}>
          <p>Bạn có chắc chắn muốn xóa Zalo Account này không?</p>
          <div style={{ textAlign: 'center' }}>
            <Button onClick={handleDeleteAccount} type='primary' htmlType='submit'>Xóa</Button>
          </div>
      </Modal>
    </>
  )
}